import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Star } from "lucide-react";
import { api, bookingsApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { gbp, prettyDate } from "@/lib/format";
import { receiptPdf } from "@/lib/pdf";

const STATUS_STYLE = {
  confirmed: { bg: "rgba(0,184,92,0.12)", color: "#00B85C", label: "Confirmed" },
  pending: { bg: "rgba(255,176,0,0.14)", color: "#B8860B", label: "Pending" },
  pending_payment: { bg: "rgba(255,176,0,0.14)", color: "#B8860B", label: "Payment due" },
  awaiting_payment: { bg: "rgba(255,176,0,0.14)", color: "#B8860B", label: "Awaiting transfer" },
  quote_requested: { bg: "rgba(10,132,255,0.12)", color: "#0A84FF", label: "Quote requested" },
  completed: { bg: "rgba(10,132,255,0.12)", color: "#0A84FF", label: "Completed" },
  cancelled: { bg: "rgba(229,23,63,0.12)", color: "#E5173F", label: "Cancelled" },
  declined: { bg: "rgba(229,23,63,0.12)", color: "#E5173F", label: "Declined" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))", label: status };
  return (
    <span className="rounded-full px-2.5 py-1 text-xs font-semibold capitalize" style={{ background: s.bg, color: s.color }} data-testid={`booking-status-${status}`}>
      {s.label}
    </span>
  );
}

// Leave a review for a completed booking (POST /reviews; one per booking).
function ReviewForm({ b, onDone }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Pick a star rating first."); return; }
    setBusy(true);
    try {
      await api("/reviews", { method: "POST", body: { listing_id: b.listing_id, booking_id: b.id, rating, text: text.trim() } });
      toast.success("Thanks for the review.");
      onDone();
    } catch (err) { toast.error(err.message || "Couldn't post the review."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 rounded-xl border border-border bg-muted/40 p-3" data-testid={"review-form-" + b.id}>
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={n + " star" + (n === 1 ? "" : "s")} onClick={() => setRating(n)} className="p-0.5" data-testid={"review-star-" + n}>
            <Star size={20} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={3} placeholder="How did it go? (optional)" className="mt-2 w-full rounded-lg border border-border bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <Button type="submit" size="sm" className="mt-2 rounded-full" disabled={busy} data-testid={"review-submit-" + b.id}>{busy ? "Posting…" : "Post review"}</Button>
    </form>
  );
}

function BookingCard({ b, onCancel, focused, onReviewed }) {
  const cancellable = ["confirmed", "pending", "pending_payment", "awaiting_payment", "quote_requested"].includes(b.status);
  const canReview = b.status === "completed" && !b.reviewed && !b.shared;
  const [reviewing, setReviewing] = useState(Boolean(focused && canReview));
  const ref = useRef(null);
  useEffect(() => { if (focused && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focused]);
  return (
    <div ref={ref} className={"flex gap-4 rounded-2xl border bg-card p-4 " + (focused ? "border-primary ring-2 ring-primary/30" : "border-border")} data-testid={`booking-${b.id}`}>
      {b.listing_image && (
        <Link to={`/listing/${b.listing_id}`} className="shrink-0">
          <img src={b.listing_image} alt="" className="h-24 w-24 rounded-xl object-cover" />
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/listing/${b.listing_id}`} className="truncate font-semibold hover:text-primary">
            {b.listing_title}
          </Link>
          <StatusBadge status={b.status} />
        </div>
        {b.service_name && <p className="mt-0.5 text-sm text-muted-foreground">{b.service_name}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {b.date && (
            <span className="inline-flex items-center gap-1">
              <Calendar size={13} /> {prettyDate(b.date)}
            </span>
          )}
          {b.time_slot && (
            <span className="inline-flex items-center gap-1">
              <Clock size={13} /> {b.time_slot}
            </span>
          )}
          {b.provider_name && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={13} /> {b.provider_name}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono-num font-semibold">{gbp(b.total ?? b.subtotal)}</span>
            <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => receiptPdf(b)} data-testid={"booking-receipt-" + b.id}>Receipt</button>
          {canReview && !reviewing && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReviewing(true)} data-testid={"booking-review-" + b.id}>Write a review</Button>
          )}
          {cancellable && (
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => onCancel(b)} data-testid={`booking-cancel-${b.id}`}>
              Cancel
            </Button>
          )}
        </div>
        {canReview && reviewing ? <ReviewForm b={b} onDone={() => { setReviewing(false); onReviewed(b.id); }} /> : null}
      </div>
    </div>
  );
}

export default function Bookings() {
  const { id: focusId } = useParams();
  const [bookings, setBookings] = useState(null);
  const reviewed = (id) => setBookings((list) => list.map((x) => (x.id === id ? { ...x, reviewed: true } : x)));

  const load = () => {
    bookingsApi
      .mine()
      .then((d) => setBookings(Array.isArray(d) ? d : []))
      .catch(() => setBookings([]));
  };
  useEffect(load, []);

  const cancel = async (b) => {
    try {
      await bookingsApi.cancel(b.id);
      toast.success("Booking cancelled");
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, status: "cancelled" } : x)));
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  const active = (bookings || []).filter((b) => !["cancelled", "completed", "declined"].includes(b.status));
  const past = (bookings || []).filter((b) => ["cancelled", "completed", "declined"].includes(b.status));

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">My bookings</h1>

      {bookings === null ? (
        <div className="mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border py-16 text-center" data-testid="bookings-empty">
          <p className="font-serif-display text-xl font-bold">No bookings yet</p>
          <p className="mt-1 text-sm text-muted-foreground">When you book something, it'll show up here.</p>
          <Link to="/marketplace">
            <Button className="mt-5 rounded-full">Explore the marketplace</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {active.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Upcoming</h2>
              <div className="space-y-4">
                {active.map((b) => (
                  <BookingCard key={b.id} b={b} onCancel={cancel} focused={b.id === focusId} onReviewed={reviewed} />
                ))}
              </div>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Past &amp; cancelled</h2>
              <div className="space-y-4 opacity-80">
                {past.map((b) => (
                  <BookingCard key={b.id} b={b} onCancel={cancel} focused={b.id === focusId} onReviewed={reviewed} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
